import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  InputAdornment,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { ArrowBack, CloudDownload, Save, CheckCircle, Search, Clear } from '@mui/icons-material';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + 'synctrack';

const BudgetMappingScreen = () => {
  const { flowInstanceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { masterFlowId: stateMasterFlowId, flowName: stateFlowName, tenantId: stateTenantId } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [masterFlowId, setMasterFlowId] = useState(stateMasterFlowId || null);
  const [tenantId, setTenantId] = useState(stateTenantId != null ? stateTenantId : (localStorage.getItem('tenantId') || '1'));
  const [flowName, setFlowName] = useState(stateFlowName || '');
  const [sourceCodes, setSourceCodes] = useState([]);
  const [targetOptions, setTargetOptions] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingTargetOptions, setLoadingTargetOptions] = useState(false);
  const [selections, setSelections] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sourceSearch, setSourceSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [openDropdownKey, setOpenDropdownKey] = useState(null);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);

  const flowId = masterFlowId || config?.masterFlowId;
  const tenantIdNum = tenantId != null ? Number(tenantId) : 1;

  const formatQbItemLabel = (o) => {
    if (!o) return '';
    const sku = o.Sku || o.Name || o.Id || '';
    const name = o.Name || '';
    if (!name || name === sku) return sku;
    if (name.startsWith(sku + ' ')) return `${sku} — ${name.slice((sku + ' ').length).trim()}`;
    return `${sku} — ${name}`;
  };

  const filteredSourceCodes = useMemo(() => {
    if (!sourceSearch.trim()) return sourceCodes;
    const q = sourceSearch.trim().toLowerCase();
    return sourceCodes.filter((row) => {
      const main = (row.budgetCodeMain || '').toLowerCase();
      const sub = (row.budgetCodeSub ?? '').toLowerCase();
      const name = (row.budgetName || '').toLowerCase();
      return main.includes(q) || sub.includes(q) || name.includes(q);
    });
  }, [sourceCodes, sourceSearch]);

  const filteredByTargetOnly = useMemo(() => {
    if (!targetSearch.trim()) return [];
    const q = targetSearch.trim().toLowerCase();
    return sourceCodes.filter((row) => {
      const main = row.budgetCodeMain || '';
      const sub = row.budgetCodeSub ?? '';
      const key = `${main}|${sub}`;
      const sel = selections[key];
      if (!sel) return false;
      const sku = (sel.Sku || '').toLowerCase();
      const name = (sel.Name || '').toLowerCase();
      const id = String(sel.Id || '').toLowerCase();
      return sku.includes(q) || name.includes(q) || id.includes(q);
    });
  }, [sourceCodes, targetSearch, selections]);

  const filteredBySourceAndTarget = useMemo(() => {
    const hasSource = sourceSearch.trim() !== '';
    const hasTarget = targetSearch.trim() !== '';
    if (!hasSource && !hasTarget) return sourceCodes;
    if (!hasSource) return filteredByTargetOnly;
    if (!hasTarget) return filteredSourceCodes;
    const sourceKeys = new Set(filteredSourceCodes.map((r) => `${r.budgetCodeMain || ''}|${r.budgetCodeSub ?? ''}`));
    const targetKeys = new Set(filteredByTargetOnly.map((r) => `${r.budgetCodeMain || ''}|${r.budgetCodeSub ?? ''}`));
    const unionKeys = new Set([...sourceKeys, ...targetKeys]);
    return sourceCodes.filter((row) => unionKeys.has(`${row.budgetCodeMain || ''}|${row.budgetCodeSub ?? ''}`));
  }, [sourceCodes, filteredSourceCodes, filteredByTargetOnly, sourceSearch, targetSearch]);

  const paginatedSourceCodes = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredBySourceAndTarget.slice(start, start + rowsPerPage);
  }, [filteredBySourceAndTarget, currentPage, rowsPerPage]);
  const totalPages = Math.max(1, Math.ceil(filteredBySourceAndTarget.length / rowsPerPage));

  const dropdownFilteredOptions = useMemo(() => {
    if (!openDropdownKey) return [];
    const q = dropdownSearch.trim().toLowerCase();
    const list = targetOptions.filter((opt) => {
      if (!q) return true;
      const sku = (opt.Sku || '').toLowerCase();
      const name = (opt.Name || '').toLowerCase();
      const id = String(opt.Id || '').toLowerCase();
      return sku.includes(q) || name.includes(q) || id.includes(q);
    });
    return [null, ...list];
  }, [openDropdownKey, targetOptions, dropdownSearch]);

  const savedTargetByKey = useMemo(() => {
    const map = {};
    (mappings || []).forEach((m) => {
      const k = `${m.sourceBudgetCodeMain || ''}|${m.sourceBudgetCodeSub ?? ''}`;
      if (m.targetId) map[k] = String(m.targetId);
    });
    return map;
  }, [mappings]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [sourceCodes.length, sourceSearch, targetSearch]);

  useEffect(() => {
    if (!flowInstanceId) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    const effectiveTenantId = stateTenantId ?? localStorage.getItem('tenantId') ?? '1';
    if (stateMasterFlowId && stateTenantId != null) {
      setMasterFlowId(stateMasterFlowId);
      setTenantId(stateTenantId);
      setFlowName(stateFlowName || '');
      setLoading(false);
      return;
    }
    fetch(`${API_BASE_URL}/datapoint/flow/${flowInstanceId}/tenant/${effectiveTenantId}`, {
      method: 'GET',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) {
          setConfig(data);
          if (data.masterFlowId) setMasterFlowId(data.masterFlowId);
          if (data.flowName) setFlowName(data.flowName);
          if (data.tenantId != null) setTenantId(data.tenantId);
          else setTenantId(effectiveTenantId);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [flowInstanceId, stateMasterFlowId, stateTenantId, stateFlowName]);

  useEffect(() => {
    if (!flowId || !tenantIdNum) return;
    const token = localStorage.getItem('token');
    Promise.all([
      fetch(`${API_BASE_URL}/datapoint/budget/flow/${flowId}/tenant/${tenantIdNum}/budget-codes`, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      }).then((r) => (r.ok ? r.json() : [])),
      fetch(`${API_BASE_URL}/datapoint/budget/flow/${flowId}/tenant/${tenantIdNum}/budget-mappings`, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      }).then((r) => (r.ok ? r.json() : [])),
    ])
      .then(([codes, savedMappings]) => {
        setSourceCodes(Array.isArray(codes) ? codes : []);
        setMappings(Array.isArray(savedMappings) ? savedMappings : []);
        const sel = {};
        (Array.isArray(savedMappings) ? savedMappings : []).forEach((m) => {
          const key = `${m.sourceBudgetCodeMain || ''}|${m.sourceBudgetCodeSub ?? ''}`;
          if (m.targetId) sel[key] = { Id: m.targetId, Sku: m.targetBudgetCode, Name: m.targetBudgetName };
        });
        setSelections(sel);
      })
      .catch((e) => {
        console.error(e);
        toast.error('Failed to load budget codes or mappings');
      });
  }, [flowId, tenantIdNum]);

  useEffect(() => {
    if (!flowId || !tenantIdNum) return;
    setLoadingTargetOptions(true);
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/datapoint/budget/flow/${flowId}/tenant/${tenantIdNum}/budget-mappings/target-options`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((options) => setTargetOptions(Array.isArray(options) ? options : []))
      .catch((e) => {
        console.error(e);
        toast.error('Failed to load QuickBooks items');
      })
      .finally(() => setLoadingTargetOptions(false));
  }, [flowId, tenantIdNum]);

  const handleImportFromAcc = async () => {
    if (!flowId || !tenantIdNum) return;
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_BASE_URL}/datapoint/budget/flow/${flowId}/tenant/${tenantIdNum}/budget-codes/import-from-acc`,
        { method: 'POST', headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (data.error) {
        toast.error(data.message || 'Import failed');
        return;
      }
      toast.success(`Imported ${data.imported ?? 0} budget codes`);
      const listRes = await fetch(
        `${API_BASE_URL}/datapoint/budget/flow/${flowId}/tenant/${tenantIdNum}/budget-codes`,
        { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } }
      );
      if (listRes.ok) setSourceCodes(await listRes.json());
    } finally {
      setImporting(false);
    }
  };

  const handleSelectQbItem = (main, sub, item) => {
    const key = `${main}|${sub ?? ''}`;
    setSelections((prev) => ({
      ...prev,
      [key]: item ? { Id: item.Id, Sku: item.Sku, Name: item.Name } : null,
    }));
  };

  const handleOpenDropdown = (key, event) => {
    setOpenDropdownKey(key);
    setDropdownAnchorEl(event.currentTarget);
    setDropdownSearch('');
  };

  const handleCloseDropdown = () => {
    setOpenDropdownKey(null);
    setDropdownAnchorEl(null);
    setDropdownSearch('');
  };

  const handleSelectFromDropdown = (main, sub, item) => {
    handleSelectQbItem(main, sub ?? null, item);
    handleCloseDropdown();
  };

  const handleSaveMapping = async (main, sub) => {
    const key = `${main}|${sub ?? ''}`;
    const sel = selections[key];
    if (!flowId || !tenantIdNum) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const isClear = !sel?.Id;
      const res = await fetch(
        `${API_BASE_URL}/datapoint/budget/flow/${flowId}/tenant/${tenantIdNum}/budget-mappings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            sourceBudgetCodeMain: main,
            sourceBudgetCodeSub: sub || undefined,
            ...(isClear ? {} : { targetId: sel.Id, targetBudgetCode: sel.Sku ?? '', targetBudgetName: sel.Name ?? '' }),
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.message || 'Save failed');
        return;
      }
      toast.success(isClear ? 'Mapping cleared' : 'Mapping saved');
      const listRes = await fetch(
        `${API_BASE_URL}/datapoint/budget/flow/${flowId}/tenant/${tenantIdNum}/budget-mappings`,
        { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } }
      );
      if (listRes.ok) setMappings(await listRes.json());
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    const toProcess = sourceCodes.filter((row) => {
      const main = row.budgetCodeMain || '';
      const sub = row.budgetCodeSub ?? '';
      const key = `${main}|${sub}`;
      const sel = selections[key];
      const savedId = savedTargetByKey[key];
      const currentId = sel?.Id != null ? String(sel.Id) : null;
      return (currentId || null) !== (savedId || null);
    });
    if (toProcess.length === 0) {
      toast.info('No changes to save.');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      let ok = 0;
      for (const row of toProcess) {
        const main = row.budgetCodeMain || '';
        const sub = row.budgetCodeSub ?? '';
        const key = `${main}|${sub}`;
        const sel = selections[key];
        const isClear = !sel?.Id;
        const res = await fetch(
          `${API_BASE_URL}/datapoint/budget/flow/${flowId}/tenant/${tenantIdNum}/budget-mappings`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              sourceBudgetCodeMain: main,
              sourceBudgetCodeSub: sub || undefined,
              ...(isClear ? {} : { targetId: sel.Id, targetBudgetCode: sel.Sku ?? '', targetBudgetName: sel.Name ?? '' }),
            }),
          }
        );
        if (res.ok) ok++;
      }
      toast.success(`Saved ${ok} change(s)`);
      const listRes = await fetch(
        `${API_BASE_URL}/datapoint/budget/flow/${flowId}/tenant/${tenantIdNum}/budget-mappings`,
        { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } }
      );
      if (listRes.ok) setMappings(await listRes.json());
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => navigate(-1);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!flowId || tenantIdNum == null) {
    return (
      <Box p={3}>
        <Alert severity="warning">Missing flow or tenant. Open this screen from the Budget-to-Item Data Point.</Alert>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  const sourceDisplay = (row) => {
    const main = row.budgetCodeMain || '';
    const sub = row.budgetCodeSub;
    return sub ? `${main}/${sub}` : main;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack}>
          Back
        </Button>
        <Typography variant="h5">Budget → QuickBooks Item Mapping</Typography>
        {flowName && <Typography variant="body2" color="text.secondary">{flowName}</Typography>}
      </Stack>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Map each source budget code to an existing QuickBooks item. Only existing QB items can be selected (no manual SKU).
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Button
            startIcon={importing ? <CircularProgress size={20} /> : <CloudDownload />}
            variant="outlined"
            size="small"
            onClick={handleImportFromAcc}
            disabled={importing}
          >
            Import from ACC
          </Button>
          <Button
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            variant="contained"
            size="small"
            onClick={handleSaveAll}
            disabled={saving}
          >
            Save all mappings
          </Button>
        </Stack>
      </Box>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr 320px 90px',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Box sx={{ gridColumn: '1 / span 2', maxWidth: 320 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search source (code or name)"
                  value={sourceSearch}
                  onChange={(e) => setSourceSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: sourceSearch && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSourceSearch('')} aria-label="Clear source search">
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search mapped target (selected QB item)"
                  value={targetSearch}
                  onChange={(e) => setTargetSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: targetSearch && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setTargetSearch('')} aria-label="Clear target search">
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box>
                <FormControl size="small" fullWidth>
                  <InputLabel>Rows per page</InputLabel>
                  <Select
                    value={rowsPerPage}
                    label="Rows per page"
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedSourceCodes.length} of {filteredBySourceAndTarget.length} rows
                {sourceSearch.trim() && targetSearch.trim()
                  ? ` · Rows matching source OR mapped target (either filter)`
                  : sourceSearch.trim()
                    ? ` · Source matches "${sourceSearch.trim()}"`
                    : targetSearch.trim()
                      ? ` · Mapped target matches "${targetSearch.trim()}"`
                      : null}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 120 }}>Source Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Source Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 280 }}>Map to QB item</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 90 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sourceCodes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    No source budget codes. Click &quot;Import from ACC&quot; to load them.
                  </TableCell>
                </TableRow>
              )}
              {sourceCodes.length > 0 && filteredBySourceAndTarget.length === 0 && (sourceSearch.trim() || targetSearch.trim()) && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    No rows match your search.
                    {sourceSearch.trim() && targetSearch.trim()
                      ? ' No row matches the source filter or the mapped target filter.'
                      : sourceSearch.trim()
                        ? ` No source matches "${sourceSearch.trim()}".`
                        : ` No mapped target matches "${targetSearch.trim()}".`}
                    {' '}Clear or change the filter(s).
                  </TableCell>
                </TableRow>
              )}
              {paginatedSourceCodes.map((row) => {
                const main = row.budgetCodeMain || '';
                const sub = row.budgetCodeSub ?? '';
                const key = `${main}|${sub}`;
                const selected = selections[key];
                const currentTargetId = selected?.Id != null ? String(selected.Id) : '';
                const savedTargetId = savedTargetByKey[key];
                const isSaved = (currentTargetId || '') === (savedTargetId || '');
                const hasUnsavedChange = (currentTargetId || null) !== (savedTargetId || null);
                const displayLabel = selected ? formatQbItemLabel(selected) : 'Select QB item';
                return (
                  <TableRow key={key}>
                    <TableCell>{sourceDisplay(row)}</TableCell>
                    <TableCell>{row.budgetName || '—'}</TableCell>
                    <TableCell sx={{ minWidth: 320, width: 320, padding: '10px 16px', verticalAlign: 'middle' }}>
                      <Box sx={{ minHeight: 48, display: 'flex', alignItems: 'center' }}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          disabled={loadingTargetOptions}
                          onClick={(e) => handleOpenDropdown(key, e)}
                          sx={{
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            fontWeight: selected ? 400 : 500,
                            color: selected ? 'text.primary' : 'text.secondary',
                            borderColor: 'divider',
                            backgroundColor: '#fff',
                            '&:hover': {
                              borderColor: 'primary.main',
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          {displayLabel}
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {isSaved ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          disabled
                          startIcon={<CheckCircle fontSize="small" />}
                        >
                          Saved
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!hasUnsavedChange || saving}
                          onClick={() => handleSaveMapping(main, sub || null)}
                        >
                          Save
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Popover
          open={Boolean(openDropdownKey)}
          anchorEl={dropdownAnchorEl}
          onClose={handleCloseDropdown}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { minWidth: 320, maxWidth: 420 } } }}
        >
          <Box sx={{ p: 1.5, pt: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by SKU or name"
              value={dropdownSearch}
              onChange={(e) => setDropdownSearch(e.target.value)}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: dropdownSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setDropdownSearch('')} aria-label="Clear search">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <List dense sx={{ maxHeight: 320, overflow: 'auto' }} disablePadding>
              {openDropdownKey && dropdownFilteredOptions.map((opt, idx) => {
                const [main, subPart] = openDropdownKey.split('|');
                const sub = subPart || null;
                const label = opt == null ? '— None —' : formatQbItemLabel(opt);
                return (
                  <ListItemButton
                    key={opt == null ? '__none__' : (opt.Id ?? idx)}
                    onClick={() => handleSelectFromDropdown(main, sub, opt)}
                  >
                    <ListItemText primary={label} primaryTypographyProps={{ noWrap: true }} />
                  </ListItemButton>
                );
              })}
              {openDropdownKey && dropdownFilteredOptions.length === 0 && (
                <ListItemButton disabled>
                  <ListItemText primary={loadingTargetOptions ? 'Loading QB items…' : 'No matching item'} />
                </ListItemButton>
              )}
            </List>
          </Box>
        </Popover>

        {filteredBySourceAndTarget.length > 0 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: 1, borderColor: 'divider', backgroundColor: '#f8f9fa' }}>
            <Typography variant="body2" color="text.secondary">
              Page {currentPage} of {totalPages}
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BudgetMappingScreen;
